import { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Chip, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Alert, Link, Tooltip, Button, LinearProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// FEAT-020 — 각 축 탭의 **세부** 부분. 요약(기존 카드) 아래에 붙는다.
//
// 🔴 요약만으로는 조치할 수 없다. "몇 %" 가 아니라 "무엇이", "몇 건" 이 아니라
//   "어디가" 를 보여야 담당자가 움직인다. 이 파일이 그 역할을 한다.

const SEV_COLOR = { critical: 'error', high: 'error', medium: 'warning', moderate: 'warning', low: 'default' };
const num = (n) => Number(n || 0).toLocaleString('ko-KR');

/* ── 규제 준수: 미준수 항목 ───────────────────────────────── */

export function ComplianceFailList({ productId, state }) {
  const [showAll, setShowAll] = useState(false);
  const row = state.data?.products?.find((p) => p.product === productId) || null;

  const { fails, unknowns } = useMemo(() => {
    const checks = row?.checks || [];
    return {
      fails: checks.filter((c) => c.status === 'fail'),
      unknowns: checks.filter((c) => c.status === 'unknown'),
    };
  }, [row]);

  if (state.loading || state.error || !row) return null;
  if (fails.length === 0 && unknowns.length === 0) {
    return <Alert severity="success" sx={{ fontSize: '0.85em' }}>미준수·확인 불가 항목이 없습니다.</Alert>;
  }

  // 차단 게이트 → 심각도 순. 고칠 순서 그대로 보여준다.
  const order = { critical: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...fails].sort(
    (a, b) =>
      (a.gate === 'block' ? 0 : 1) - (b.gate === 'block' ? 0 : 1) ||
      (order[a.severity] ?? 9) - (order[b.severity] ?? 9),
  );
  const shown = showAll ? sorted : sorted.slice(0, 10);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.75 }}>
        <Typography variant="subtitle2" fontWeight={700}>미준수 항목</Typography>
        <Chip label={`${fails.length}건`} size="small" color="error" />
        {unknowns.length > 0 && (
          <Tooltip title="확인 수단이 없어 판정하지 못한 항목입니다 — 준수가 아닙니다" arrow>
            <Chip label={`확인 불가 ${unknowns.length}건`} size="small" variant="outlined" />
          </Tooltip>
        )}
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 620 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>항목</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>통제</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>심각도</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>확인된 상태</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell>
                  <Typography variant="body2">{c.title}</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.secondary' }}>
                    {c.id}
                    {c.gate === 'block' && (
                      <Chip label="차단" size="small" color="error" sx={{ ml: 0.75, fontSize: 10, height: 17 }} />
                    )}
                    {c.actionable === false && (
                      <Tooltip title="권한·라이선스 등으로 담당자가 직접 고칠 수 없는 항목입니다" arrow>
                        <Chip label="소관 밖" size="small" variant="outlined" sx={{ ml: 0.5, fontSize: 10, height: 17 }} />
                      </Tooltip>
                    )}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {(c.controls || []).join(' · ') || '—'}
                </TableCell>
                <TableCell>
                  <Chip label={c.severity || '—'} size="small" color={SEV_COLOR[c.severity] || 'default'} variant="outlined" sx={{ fontSize: 10, height: 19 }} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'text.secondary', maxWidth: 260, wordBreak: 'break-all' }}>
                  {c.detail || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {sorted.length > 10 && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ alignSelf: 'flex-start' }}>
          {showAll ? '접기' : `나머지 ${sorted.length - 10}건 더 보기`}
        </Button>
      )}
    </Stack>
  );
}

/* ── 보안: 남아 있는 취약점 ───────────────────────────────── */

export function SecurityAlertList({ productId, state }) {
  const [showAll, setShowAll] = useState(false);
  const row = state.data?.products?.find((p) => p.id === productId) || null;

  if (state.loading || state.error || !row || row.missing) return null;
  const alerts = row.alerts || [];
  if (alerts.length === 0) {
    return <Alert severity="success" sx={{ fontSize: '0.85em' }}>열려 있는 취약점 알림이 없습니다.</Alert>;
  }

  const order = { critical: 0, high: 1, moderate: 2, medium: 2, low: 3 };
  const sorted = [...alerts].sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9));
  const shown = showAll ? sorted : sorted.slice(0, 10);

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" fontWeight={700}>남아 있는 취약점</Typography>
        <Chip label={`${alerts.length}건`} size="small" color="error" />
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>심각도</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>패키지</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>내용</TableCell>
              <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>저장소</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((a, i) => (
              <TableRow key={`${a.package || a.summary || i}-${i}`} hover>
                <TableCell>
                  <Chip label={a.severity || '—'} size="small" color={SEV_COLOR[a.severity] || 'default'} variant="outlined" sx={{ fontSize: 10, height: 19 }} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {a.package || '—'}
                </TableCell>
                <TableCell sx={{ fontSize: 12, maxWidth: 320 }}>
                  {a.url ? (
                    <Link href={a.url} target="_blank" rel="noreferrer">{a.summary || a.url}</Link>
                  ) : (a.summary || '—')}
                </TableCell>
                <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {a.repo || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {sorted.length > 10 && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ alignSelf: 'flex-start' }}>
          {showAll ? '접기' : `나머지 ${sorted.length - 10}건 더 보기`}
        </Button>
      )}
    </Stack>
  );
}

/* ── 사용량: 국가별 분포 ──────────────────────────────────── */

export function UsageCountryList({ productId, state }) {
  const [showAll, setShowAll] = useState(false);
  const row = state.data?.products?.find((p) => p.id === productId) || null;
  const cov = state.data?.coverage;

  if (state.loading || state.error || !row) return null;

  const countries = row.countries || [];
  const total = row.countries_total || 0;

  // 🔴 국가 분해가 없던 기간을 '그 나라에서 안 쓴다' 로 읽으면 안 된다.
  if (countries.length === 0) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.85em' }}>
        국가별 분포가 아직 없습니다 — 수집기에 국가 차원이 들어간 뒤부터 쌓입니다.
        {cov?.days_observed ? ` (이 기간 호스트 적재 ${cov.days_observed}일, 국가 분해 ${cov.country_days_observed || 0}일)` : ''}
      </Alert>
    );
  }

  const shown = showAll ? countries : countries.slice(0, 12);
  const max = countries[0]?.requests || 1;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ gap: 0.75 }}>
        <Typography variant="subtitle2" fontWeight={700}>국가별 분포</Typography>
        <Typography variant="caption" color="text.secondary">
          {countries.length}개국 · 합계 {num(total)}
          {cov && cov.country_days_observed != null && cov.country_days_observed < cov.days_observed && (
            <> · <b>이 기간 {cov.days_observed}일 중 {cov.country_days_observed}일만 국가별로 관측</b></>
          )}
          {cov?.country_truncated_days > 0 && ` · ${cov.country_truncated_days}일은 상위 조합만 수집됨`}
        </Typography>
      </Stack>

      {/* 🔴 국가 합계와 운영 요청 합계가 다를 수 있다. 분모를 잘못 잡지 않도록 밝힌다. */}
      {total > 0 && row.live_requests > 0 && total !== row.live_requests && (
        <Typography variant="caption" color="text.secondary">
          국가 합계({num(total)})는 이 제품의 운영 요청({num(row.live_requests)})과 다릅니다 —
          국가 분해가 관측된 기간만 합산한 값입니다. 비율은 국가 합계를 분모로 보세요.
        </Typography>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 0, p: 1.5 }}>
        <Stack spacing={0.75}>
          {shown.map((c) => (
            <Box key={c.country} sx={{ display: 'grid', gridTemplateColumns: '52px 1fr 110px', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
                {c.country === 'XX' ? '미상' : c.country}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.max(1, (c.requests / max) * 100)}
                sx={{ height: 8, borderRadius: 0 }}
              />
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', textAlign: 'right' }}>
                {num(c.requests)}
                {total > 0 && ` (${((c.requests / total) * 100).toFixed(1)}%)`}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
      {countries.length > 12 && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ alignSelf: 'flex-start' }}>
          {showAll ? '접기' : `나머지 ${countries.length - 12}개국 더 보기`}
        </Button>
      )}
      <Typography variant="caption" color="text.secondary">
        요청 수는 <b>봇을 포함</b>합니다 — 호스트 차원 데이터에는 사람/기계 구분이 없습니다. 사람 수로 읽지 마세요.
      </Typography>
    </Stack>
  );
}
